import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from "react-redux";
import { AppRootState } from "../redux/reducers";
import CenteredLoader from "../common/components/CenteredLoader";
import { fetchResults } from '../common/services/api'; // Assuming this function exists
import { formatDateToUS, formatAnyDateToLong, formatAnyDateToUS, formatAnyDateToTime } from '../common/utils';

// --- Define a more specific type for your results data ---
interface ResultDetails {
  // Add properties based on what 'current' contains, e.g.:
  result: string;
  // ... other properties
}

interface ResultsMetadata {
  patientEmail: string;
  kitId: string;
  collectionDateTime: string | null; // ISO string or similar
  resultsTimeStamp: number | null; // Assuming Unix timestamp
  procedureDate: string | null; // ISO string or similar
}

interface ResultsData {
  current: ResultDetails | null;
  history: any[]; // Define more specifically if needed
  results: ResultsMetadata | null;
}

// --- Define a possible structure for API errors ---
// (Adjust based on how fetchResults actually throws errors)
interface ApiError extends Error {
  status?: number; // Assuming the status code is added to the error
  response?: { // Alternative structure, common with libraries like Axios
    status: number;
    data?: any;
  };
  // Add other potential properties from your error handling
}

const Results: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState<ResultsData | null>(null);
  // --- State for specific error messages ---
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Safely get serial_number from state
  const serialNumber = location.state?.serial_number as string | undefined;
  const userData: any = useSelector((state: AppRootState) => state.userData); // Keep 'any' if structure is complex/unknown, otherwise type it

  useEffect(() => {
    const getResults = async () => {
      if (!serialNumber) {
        console.warn('No serial number found in location state. Navigating home.');
        navigate('/');
        return;
      }

      // Reset error message on new fetch attempt
      setErrorMessage(null);
      setLoading(true);

      try {
        const resultsData = await fetchResults(serialNumber); // Assume this might throw ApiError

        // Handle case where API might successfully return null/undefined/empty
        if (resultsData?.results) { // Check for essential data presence
          setResults(resultsData as ResultsData); // Cast if fetchResults returns 'any'
        } else {
          console.warn('Received empty or invalid results data from API.');
          // Decide how to handle: navigate or show message?
          // Option 1: Navigate
          // navigate('/');
          // Option 2: Show specific message
          setErrorMessage("Could not retrieve valid result data.");
          setResults(null);
        }

      } catch (error: unknown) { // Catch error as 'unknown'
        console.error('Error fetching results:', error);

        // --- Check for the specific 403 Forbidden error ---
        const apiError = error as ApiError; // Type assertion
        const status = apiError.status ?? apiError.response?.status;

        if (status === 403) {
          // Set the specific message for clinic ID mismatch
          setErrorMessage("Your results were delivered to your provider. Please contact them if you wish to view them.");
          setResults(null); // Ensure no previous results are shown
        } else {
          // Handle other errors (e.g., 404, 500, network error) - navigate home for now
          // You could add more specific messages based on other status codes here
          // For example: if (status === 404) setErrorMessage("Results not found for this kit.");
          navigate('/'); // Or set a generic error message: setErrorMessage("An error occurred while fetching results.")
        }
      } finally {
        // Ensure loading is always set to false after attempt completes
        setLoading(false);
      }
    };

    getResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialNumber, navigate]); // Keep navigate in dependency array as per eslint suggestion


  // --- Loading State ---
  if (loading) {
    return (
      <div className="mt-8 flow-root bg-white">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="overflow-hidden shadow">
              <CenteredLoader className="flex mt-24 mb-24 bg-white" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- Error Message Display ---
  if (errorMessage) {
    return (
      <div className="w-full min-h-screen bg-gray-100 text-gray-900 flex items-center justify-center p-8">
        <div className="bg-white p-8 rounded shadow-md text-center mb-64">
          <h1 className="text-2xl font-semibold mb-4 text-red-600">Access Denied</h1>
          <p className="text-lg">{errorMessage}</p>
          {/* Optional: Add a button to navigate back or contact support */}
          <button
            onClick={() => navigate('/')} // Navigate home or to another relevant page
            className="mt-6 px-4 py-2 bg-brand-blue-400 text-white rounded hover:bg-brand-blue-500"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  // --- No Results Data (after loading and no error) ---
  // This might be redundant if errors/empty data lead to navigation or errorMessage
  if (!results) {
    console.error("Rendering null because results are null and no error message is set.");
    return null; // Or return a "No results found" component
  }

  // --- Print styles constant ---
  const PRINT_STYLES = `
     body { 
      font-family: Arial, sans-serif; 
      margin: 0; 
      color: #333; 
      background: #f3f4f6;
      line-height: 1.6;
      padding: 20px;
    }
    .print-results-container { 
        max-width: 800px; 
        margin: 0 auto;
        background: white;
        box-shadow: 0 0 10px rgba(0,0,0,0.1);
        padding: 20px;
      }
    img { 
      max-width: 400px; 
      width: 100%;
      height: auto; 
      margin: 0 auto;
      display: block;
    }
    .grid { 
        display: grid; 
        grid-template-columns: 1fr 1fr; 
        gap: 30px;
        margin-top: 20px;
      }
    .space-y-6 > div {
        margin-bottom: 20px;
      }
    .text-brand-blue-400 { 
        color: #45A3DB; 
        font-size: 16px;
        font-weight: bold;
        margin-bottom: 12px;
        border-bottom: 2px solid #45A3DB;
        padding-bottom: 4px;
      }
    .bg-gray-50 { 
        background: #f9f9f9; 
        padding: 12px; 
        border-radius: 4px;
        border-left: 4px solid #45A3DB;
        font-size: 15px;
        line-height: 1.4;
      }
    .text-xs { 
        font-size: 12px; 
        color: #666; 
        line-height: 1.3;
        text-align: justify;
        border-top: 2px solid #45A3DB;
        padding-top: 15px;
        margin-top: 25px;
      }
    ul { 
      list-style: none;
      margin: 0;
      padding: 0;
    }
    li { 
        margin-bottom: 6px;
        font-size: 15px;
        line-height: 1.3;
      }
    .font-bold, strong { 
      font-weight: bold;
      color: #333;
    }
    .flex { 
        display: flex; 
        align-items: center; 
        gap: 8px;
        padding: 6px;
        border: 1px solid #ddd;
        border-radius: 4px;
        background: #f9f9f9;
        font-size: 13px;
        margin-bottom: 8px;
      }
    .flex.checked {
      background: #e6f3ff;
      border-color: #45A3DB;
    }
    input[type="checkbox"] { 
      width: 16px; 
      height: 16px; 
      accent-color: #45A3DB;
    }
    .pl-28 {
      padding-left: 7rem;
    }
    .mt-10 {
        margin-top: 15px;
      }
    .pt-5 {
        padding-top: 15px;
      }
    .px-8 {
      padding-left: 2rem;
      padding-right: 2rem;
    }
    .pb-8 {
        padding-bottom: 1rem;
      }
    .mb-8 {
        margin-bottom: 20px;
      }
    .py-5 {
        padding-top: 15px;
        padding-bottom: 15px;
      }
    .mt-6 {
        margin-top: 15px;
      }
    .mt-8 {
        margin-top: 20px;
      }
    .space-y-2 > * + * {
      margin-top: 8px;
    }
    .space-y-6 > * + * {
        margin-top: 20px;
      }
    @media print {
      body { 
        background: white;
        padding: 0;
      }
      .print-results-container {
        box-shadow: none;
        padding: 20px;
      }
      .text-xs {
        page-break-inside: avoid;
      }
      .grid {
        page-break-inside: avoid;
      }
    }
  `;

  // --- Print function ---
  const handlePrint = (): void => {
    try {
      const printContent = document.querySelector('.print-results-container');
      if (!printContent) {
        console.error('Print content not found');
        console.error('Could not find content to print');
        return;
      }
  
      const printWindow = window.open('', '_blank', 'width=800,height=600');
      if (!printWindow) {
        console.error('Failed to open print window - popup blocked?');
        console.error('Print window blocked. Please allow popups for this site.');
        return;
      }
  
      const doc = printWindow.document;
      const htmlEl = doc.createElement('html');
      htmlEl.innerHTML = `
        <head>
          <title>Test Results</title>
          <style>${PRINT_STYLES}</style>
        </head>
        <body>
          ${printContent.outerHTML}
        </body>
      `;
  
      // Avoid deprecated document.write and ensure documentElement exists
      doc.open();
      doc.replaceChildren(htmlEl);
      doc.close();
      printWindow.focus();
      
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 500);
    } catch (error) {
      console.error('Print error:', error);
      console.error('Error generating PDF. Please try again.');
    }
  };

  return (
    <div className="p-5 bg-gray-100 min-h-screen">
      <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-lg overflow-hidden">
        {/* Print Button */}
        <div className="text-end py-6 px-6">
          <button
            onClick={handlePrint}
            className="bg-brand-blue-400 text-white px-5 py-2.5 rounded font-bold text-sm transition-colors duration-200 hover:bg-brand-blue-500"
          >
            Print Results
          </button>
        </div>

        <div className='print-results-container p-6'>
          {/* Logo Section */}
          <img
            className="mx-auto mt-6 w-auto h-48 md:h-72 object-contain" // Adjusted height/object-fit
            src={results?.current?.result?.includes("notFertile") ? "../../images/JackFinalGif.gif" : "../../images/jack-logo.png"}
            alt="Result indicator" // More descriptive alt text
          />

          {/* Results Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mt-8">
            {/* Left Column */}
            <div className="space-y-6">
              {/* Patient Information */}
              <div>
                <h3 className="text-brand-blue-400 text-base font-bold mb-4 pb-1 border-b-2 border-brand-blue-400">
                  Patient Information:
                </h3>
                <ul className="space-y-2 text-sm leading-relaxed">
                  <li><strong>Patient Name:</strong> {userData?.given_name} {userData?.family_name}</li>
                  <li><strong>Kit ID#:</strong> {results?.results?.kitId ?? 'N/A'}</li>
                  <li><strong>Test Type:</strong> Post-Vasectomy Semen Test</li>
                  <li>
                    <strong>Vasectomy Date:</strong> {formatAnyDateToLong(results?.results?.procedureDate)}
                  </li>
                  <li>
                    <strong>Date of Birth:</strong> {formatDateToUS(userData?.birthdate)}
                  </li>
                </ul>
              </div>

              {/* Result Section */}
              <div>
                <h3 className="text-brand-blue-400 text-base font-bold mb-4 pb-1 border-b-2 border-brand-blue-400">
                  Result:
                </h3>
                <div className="space-y-2">
                  <ResultCheckbox
                    label="Negative (No Sperm Detected)"
                    checked={results?.current?.result === 'notFertile' || results?.current?.result === 'notFertileNoSperm'}
                  />
                  <ResultCheckbox
                    label="Negative (Non-motile Sperm Detected)"
                    checked={results?.current?.result === 'notFertileNonMotile'}
                  />
                  <ResultCheckbox
                    label="Positive (Sperm Detected) - Patient Follow-up Needed"
                    checked={results?.current?.result === 'fertile'}
                  />
                  <ResultCheckbox
                    label="Questionable - See Comments"
                    checked={results?.current?.result === 'questionable'}
                  />
                  <ResultCheckbox
                    label="Error - See Comments"
                    checked={results?.current?.result === 'error'}
                  />
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              {/* Sample Information */}
              <div>
                <h3 className="text-brand-blue-400 text-base font-bold mb-4 pb-1 border-b-2 border-brand-blue-400">
                  Sample Information:
                </h3>
                <ul className="space-y-2 text-sm leading-relaxed">
                  <li>
                    <strong>Collection Date:</strong> {formatAnyDateToUS(results?.results?.collectionDateTime)}
                  </li>
                  <li>
                    <strong>Collection Time:</strong> {results?.results?.collectionDateTime
                      ? new Date(results.results.collectionDateTime).toLocaleTimeString(navigator.language)
                      : 'N/A'}
                  </li>
                  <li>
                    <strong>Lab Testing Date:</strong> {formatAnyDateToUS(results?.results?.resultsTimeStamp ? results.results.resultsTimeStamp * 1000 : null)}
                  </li>
                  <li>
                    <strong>Lab Testing Time:</strong> {formatAnyDateToTime(results?.results?.resultsTimeStamp ? results.results.resultsTimeStamp * 1000 : null)}
                  </li>
                  <li><strong>Sample Type:</strong> Semen</li>
                  <li><strong>Sample Source:</strong> Ejaculate</li>
                  <li><strong>Test Performed at:</strong> Androlab Inc</li>
                  <li className="pl-28">1450 Summit Avenue, Suite 155</li>
                  <li className="pl-28">Oakbrook Terrace, IL 60181</li>
                </ul>
              </div>

              {/* Comments Section */}
              <div>
                <h3 className="text-brand-blue-400 text-base font-bold mb-4 pb-1 border-b-2 border-brand-blue-400">
                  Comments:
                </h3>
                <div className="bg-gray-50 p-4 rounded border-l-4 border-brand-blue-400 text-sm leading-relaxed">
                  {getCommentForResult(results?.current?.result)}
                </div>
              </div>
            </div>
          </div>

          {/* Footer Disclaimer */}
          <div className="mt-8 pt-4 border-t-2 border-brand-blue-400 px-6 pb-6">
            <p className="text-xs text-gray-600 leading-relaxed text-justify">
              Jack At-Home Semen Test performs qualitative semen analysis as a post-vasectomy test. 
              Samples are examined by certified laboratory personnel in our CLIA lab, using 60 high-power 
              fields (hp~ under 10x eye piece X 40x objective for a total magnification of 400x). 
              Comments are given in order to aid the customer and/or provider in ruling out a failed vasectomy. 
              Comments do not replace laboratory results and caution must be given when correlating subsequent specimens. 
              Patients may be referred elsewhere for qualitative semen analysis if needed.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Helper Component for Checkboxes ---
interface ResultCheckboxProps {
  label: string;
  checked: boolean | undefined;
}

const ResultCheckbox: React.FC<ResultCheckboxProps> = ({ label, checked }) => (
  <div className={`flex items-center gap-3 p-2 border rounded text-sm ${checked ? 'bg-blue-50 border-brand-blue-400' : 'bg-gray-50 border-gray-300'}`}>
    <input
      type="checkbox"
      readOnly
      checked={!!checked}
      className="w-4 h-4 text-brand-blue-400 focus:ring-0 cursor-default"
    />
    <span className="text-sm">{label}</span>
  </div>
);

// --- Helper function for comments ---
const getCommentForResult = (result: string | undefined): string => {
  switch (result) {
    case 'notFertile':
    case 'notFertileNoSperm':
      return 'No sperm detected in the sample. This is consistent with a successful vasectomy.';
    case 'notFertileNonMotile':
      return 'A well-mixed uncentrifuged fresh post vasectomy semen showed only rare non-motile sperm (≤ 100,000 sperm/ml). Based on AUA Vasectomy Guidelines - your vasectomy procedure appears to have been successful.';
    case 'fertile':
      return 'Sperm concentration (count) could be determined on a counting chamber. If it has been more than 12 weeks since the date of your vasectomy procedure it is recommended that you schedule a follow up with the clinic that performed your vasectomy procedure.';
    case 'questionable':
      return 'One well-mixed uncentrifuged fresh post vasectomy semen shows greater than 100,000 non-motile sperm/ml. If it has been more than 12 weeks since the date of your vasectomy procedure, it is recommended that you schedule a follow up with the clinic that performed your vasectomy procedure.';
    case 'error':
      return 'Your sample was deemed ineligible by our lab for proper post vasectomy analysis due to the condition it was received or failure to follow instructions. Customer support has been notified and will be reaching out to you within the next 24 hours. If you do not hear from Customer Support in the next 24 hours please reach out to determine next steps.';
    default:
      return 'No comments available.';
  }
};

export default Results;